import { useMemo, useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone'

import {
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { MenuItem, Box, IconButton, Tooltip, Button, TextField, Typography, Alert, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import KeyOffIcon from '@mui/icons-material/KeyOff'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'
import KeyIcon from '@mui/icons-material/Key'
import EditIcon from '@mui/icons-material/Edit'

import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { addApiKey, deleteApiKey, getApiKeys, setApiKeyStatus, toggleApiKeyClientFunctionAccess } from '../../providers/clients-api'

import DataExportDialog from '/components/data-export-dialog'
import ClickToCopy from '/components/click-to-copy'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import excelExport from './key-list-to-excel'
import { confirm } from '/components/confim-dialog'
import StudioTable, { useStudioTable } from '../studio-table'

const defaultCustomApiAccessEnabled = false

const formatCreateDate = value => value
  ? moment.utc(value).tz('Europe/London').format('DD MMM YYYY HH:mm')
  : '-'

const baseKeyUsageOptions = [
  { value: 'custom', label: 'Custom' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'zapier', label: 'Zapier' }
]

const APIKeyAdmin = () => {

  const { apiSource, apiKey, setProcessing, selectedClientId } = useContext(AppContext)

  const [loading, setLoading] = useState(true)
  const [keys, setKeys] = useState([])
  const [apiAccessEnabled, setApiAccessEnabled] = useState(defaultCustomApiAccessEnabled)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false)
  const [keyName, setKeyName] = useState("")
  const [keyUsage, setKeyUsage] = useState('custom')
  const [ipAllowList, setIpAllowList] = useState('')
  const [editingKey, setEditingKey] = useState(null)
  const [editIpAllowList, setEditIpAllowList] = useState('')
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const keyUsageOptions = useMemo(() => baseKeyUsageOptions, [])

  useEffect(() => {

    (async () => {
      setLoading(true)
      const result = await getApiKeys({ apiSource, apiKey })

      if (result.ok) {
        setKeys(result.keys || [])
        setApiAccessEnabled(result.custom_api_access_enabled || defaultCustomApiAccessEnabled)
      }
      setLoading(false)
    })()

  }, [selectedClientId, apiSource, apiKey])

  const addKey = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Adding new API key', variant: 'light' })
    const result = await addApiKey({ apiSource, apiKey, keyName, keyUsage, ipAllowList: keyUsage === 'custom' ? ipAllowList : '' })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'API Key added', variant: 'success' })
      setKeys(result.keys)
      setKeyName("")
      setKeyUsage('custom')
      setIpAllowList('')
      setNewKeyDialogOpen(false)
    } else {
      showActionError(result.error, 'Error adding API key')
    }
  }

  const deleteKey = async keyId => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this key rather than temporarily disable it?'
    })

    if (confirmed) {
      setProcessing(true)
      enqueueSnackbar({ message: 'Removing API Key', variant: 'light' })
      const result = await deleteApiKey({ apiSource, apiKey, keyId })
      setProcessing(false)

      if (result.ok) {
        enqueueSnackbar({ message: 'API Key removed', variant: 'success' })
        setKeys(result.keys)
      } else {
        showActionError(result.error, 'Error removing API key')
      }
    }
  }

  const toggleKeyEnabled = async keyId => {
    const keyRecord = keys.find(k => k.key_id === keyId)
    const status = 1 - keyRecord.key_enabled

    setProcessing(true)
    enqueueSnackbar({ message: 'Setting key status', variant: 'light' })
    const result = await setApiKeyStatus({ apiSource, apiKey, keyId, status })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'Key status saved', variant: 'success' })
      setKeys(result.keys)
    } else {
      showActionError(result.error, 'Error setting API key status')
    }
  }

  const toggleClientFunctionAccess = async keyId => {
    const result = await toggleApiKeyClientFunctionAccess({ apiSource, apiKey, keyId })
    if (result.ok) {
      setKeys(result.keys)
    } else {
      showActionError(result.error, 'Error setting client function access')
    }
  }

  const openEditDialog = keyRecord => {
    setEditingKey(keyRecord)
    setEditIpAllowList(keyRecord.key_usage === 'custom' ? (keyRecord.ip_allow_list ?? []).join('\n') : '')
  }

  const closeEditDialog = () => {
    setEditingKey(null)
    setEditIpAllowList('')
  }

  const saveKeySettings = async () => {
    if (!editingKey) return

    setProcessing(true)
    enqueueSnackbar({ message: 'Saving API key settings', variant: 'light' })
    const result = await setApiKeyStatus({
      apiSource,
      apiKey,
      keyId: editingKey.key_id,
      status: editingKey.key_enabled,
      clientFunctionAccessStatus: editingKey.key_usage === 'custom' ? editingKey.allow_access_to_client_functions : 0,
      ipAllowList: editingKey.key_usage === 'custom' ? editIpAllowList : undefined
    })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'API key settings saved', variant: 'success' })
      setKeys(result.keys)
      closeEditDialog()
    } else {
      showActionError(result.error, 'Error saving API key settings')
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'key_id',
      header: 'Key ID',
      filterVariant: 'autocomplete',
      size: 80,
    },
    {
      accessorKey: 'key_name',
      header: 'Key name',
      filterVariant: 'autocomplete',
      size: 200,
      Cell: ({ cell }) => <ClickToCopy source={cell.getValue()} successMessage="Key name copied">{cell.getValue()}</ClickToCopy>,
    },
    {
      accessorKey: 'create_date',
      header: 'Created',
      size: 160,
      Cell: ({ cell }) => formatCreateDate(cell.getValue()),
    },
    {
      accessorKey: 'key_enabled',
      header: 'Enabled',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'allow_access_to_client_functions',
      header: 'Client Function Access',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell, row }) => row.original.key_usage === 'custom'
        ? (cell.getValue() ? "yes" : "no")
        : "-"
    },
    {
      accessorKey: 'key_usage',
      header: 'Usage',
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: keyUsageOptions.map(option => ({ label: option.label, value: option.value })),
      size: 140,
      Cell: ({ cell }) => keyUsageOptions.find(option => option.value === cell.getValue())?.label || cell.getValue()
    },
    {
      accessorKey: 'ip_allow_list',
      header: 'Allowed IPs',
      filterVariant: 'autocomplete',
      size: 220,
      Cell: ({ cell, row }) => row.original.key_usage === 'custom' ? formatIpAllowList(cell.getValue()) : "-"
    },
    {
      accessorKey: 'key',
      header: 'API Key',
      filterVariant: 'autocomplete',
      size: 200,
      Cell: ({ cell }) => <ClickToCopy source={cell.getValue()} successMessage="API key copied">{cell.getValue()}</ClickToCopy>,
    },
  ], [keyUsageOptions])

  const table = useStudioTable({
    tableKey: 'api-keys',
    columns,
    data: keys,
    state: {
      isLoading: loading
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: originalRow => originalRow.key_id,
    renderRowActionMenuItems: ({ closeMenu, row }) => getActions(toggleKeyEnabled, toggleClientFunctionAccess, deleteKey, openEditDialog, closeMenu, row),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <Button startIcon={<KeyIcon />} onClick={() => setNewKeyDialogOpen(true)} variant='contained'>New API key</Button>
    )
  })
  const tableHeight = '100%'

  return (
    <>
      <Box sx={{ height: tableHeight, width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!loading && !apiAccessEnabled &&
          <div className='flex flex-col gap-4 p-5 max-w-[900px]'>
            <Typography variant='h5'>Custom API access is currently disabled</Typography>
            <Alert severity='warning'>
              Custom API access is disabled for this account. Please contact <Link to='/support/history' className='link'>support</Link> if API access is required.
            </Alert>
          </div>
        }
        <StudioTable table={table} />
      </Box>

      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName="api_keys" data={keys} excelExport={excelExport} table={table} />
      <Dialog open={newKeyDialogOpen} onClose={() => setNewKeyDialogOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>New API key</DialogTitle>
        <DialogContent>
          <div className='flex flex-col gap-4 pt-2'>
            <TextField value={keyName} onChange={e => setKeyName(e.target.value)} label="Key name" size='small' required autoFocus />
            <TextField
              select
              value={keyUsage}
              onChange={e => setKeyUsage(e.target.value)}
              label="Key usage"
              size='small'
            >
              {keyUsageOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              value={ipAllowList}
              onChange={e => setIpAllowList(e.target.value)}
              label="Allowed IPs"
              helperText={keyUsage === 'custom' ? "One IP or CIDR per line" : "Only available for custom keys"}
              multiline
              minRows={2}
              disabled={keyUsage !== 'custom'}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewKeyDialogOpen(false)}>Cancel</Button>
          <Button variant='contained' startIcon={<KeyIcon />} onClick={addKey} disabled={keyName.trim().length < 3}>Create API key</Button>
        </DialogActions>
      </Dialog>
      <EditKeyDialog
        open={Boolean(editingKey)}
        onClose={closeEditDialog}
        editingKey={editingKey}
        ipAllowList={editIpAllowList}
        setIpAllowList={setEditIpAllowList}
        saveKeySettings={saveKeySettings}
      />
      <ActionErrorDialog title="API key action failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default APIKeyAdmin

const getActions = (toggleKeyEnabled, toggleClientFunctionAccess, deleteKey, openEditDialog, closeMenu, row) => {
  const actions = [
    <MenuItem key="toggle" onClick={() => { toggleKeyEnabled(row.original.key_id); closeMenu() }}><KeyIcon className='mr-2' />{row.original.key_enabled === 1 ? "Disable" : "Enable"} Key</MenuItem>,
    <MenuItem key="edit" onClick={() => { openEditDialog(row.original); closeMenu() }}><EditIcon className='mr-2' /> Edit Key Settings</MenuItem>,
    <MenuItem key="delete" onClick={() => { deleteKey(row.original.key_id); closeMenu() }}><KeyOffIcon className='mr-2' /> Delete Key</MenuItem>,
  ]

  if (row.original.key_usage === 'custom') {
    actions.splice(1, 0, <MenuItem key="admin" onClick={() => { toggleClientFunctionAccess(row.original.key_id); closeMenu() }}><SupervisorAccountIcon className='mr-2' />{row.original.allow_access_to_client_functions === 1 ? "Disable" : "Enable"} Client Function Access for Key</MenuItem>)
  }

  return actions
}

const formatIpAllowList = value => {
  if (!Array.isArray(value) || value.length === 0) return 'Any'
  return value.join(', ')
}

const EditKeyDialog = ({ open, onClose, editingKey, ipAllowList, setIpAllowList, saveKeySettings }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Edit API key settings</DialogTitle>
    <DialogContent>
      <div className='flex flex-col gap-5 pt-2'>
        <TextField
          value={editingKey?.key_usage ?? '-'}
          label="key usage"
          slotProps={{ inputLabel: { shrink: true } }}
          disabled
        />
        <TextField
          value={ipAllowList}
          onChange={e => setIpAllowList(e.target.value)}
          label="allowed IPs"
          helperText={editingKey?.key_usage === 'custom' ? "One IP or CIDR per line" : "Only available for custom keys"}
          multiline
          minRows={4}
          disabled={editingKey?.key_usage !== 'custom'}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </div>
    </DialogContent>
    <DialogActions>
      <Button variant='outlined' onClick={onClose}>Cancel</Button>
      <Button variant='contained' onClick={saveKeySettings}>Save</Button>
    </DialogActions>
  </Dialog>
)
